import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("auth.pwdTooShort")); return; }
    if (password !== confirm) { setError(t("auth.pwdMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
        <div className="font-sans text-xl tracking-[0.25em] mb-10">OMNIA</div>
        {done ? (
          <div data-testid="reset-done" className="space-y-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            <h2 className="text-2xl font-medium tracking-tight">{t("auth.passwordUpdatedTitle")}</h2>
            <p className="text-navy/60">{t("auth.redirecting")}</p>
          </div>
        ) : !token ? (
          <div className="space-y-4">
            <h2 className="text-2xl font-medium tracking-tight">{t("auth.invalidLinkTitle")}</h2>
            <p className="text-navy/60">{t("auth.invalidLinkBody")}</p>
            <Link to="/forgot-password" className="text-violet hover:underline">{t("auth.requestNewLink")}</Link>
          </div>
        ) : (
          <>
            <div className="overline text-navy/40 mb-3">{t("auth.newPassword")}</div>
            <h2 className="text-3xl font-medium tracking-tight mb-8">{t("auth.resetTitle")}</h2>
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder={t("auth.newPassword")}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required placeholder={t("auth.confirmPasswordPlaceholder")}
                className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              {error && <p data-testid="reset-error" className="text-sm text-destructive">{error}</p>}
              <button data-testid="reset-submit" type="submit" disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white py-3.5 hover:bg-navy-soft transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.updatePassword")}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
